// src/components/Login.tsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { login } from '../api/authService'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from './LoadingSpinner'
import toast from 'react-hot-toast'
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import axios from 'axios'
import logo from '../assets/images/logo.png'
import { Input } from './ui'
import { FormField } from './ui/Form'

interface LoginProps {
  onLogin: (token: string, role: string) => void
}

/**
 * Convierte errores de la API a mensajes amigables en español
 */
const getErrorMessage = (error: unknown): string => {
  // Error de Axios con respuesta del servidor
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const message = error.response?.data?.message || error.response?.data?.error

    // Si hay un mensaje específico del servidor, intentar usarlo
    if (message && typeof message === 'string') {
      // Traducir mensajes comunes del backend si vienen en inglés
      const translatedMessages: Record<string, string> = {
        'Invalid credentials': 'Credenciales inválidas',
        'User not found': 'Usuario no encontrado',
        'Invalid password': 'Contraseña incorrecta',
        'Email is required': 'El email es requerido',
        'Password is required': 'La contraseña es requerida',
        'Unauthorized': 'No autorizado',
        'Forbidden': 'Acceso denegado',
      }

      return translatedMessages[message] || message
    }

    // Mensajes según código de estado HTTP
    switch (status) {
      case 400:
        return 'Por favor, verifica que los datos ingresados sean correctos.'
      case 401:
        return 'Email o contraseña incorrectos. Por favor, intenta nuevamente.'
      case 403:
        return 'No tienes permisos para acceder. Contacta al administrador.'
      case 404:
        return 'El servicio de autenticación no está disponible.'
      case 429:
        return 'Demasiados intentos. Por favor, espera unos minutos antes de intentar nuevamente.'
      case 500:
        return 'Error en el servidor. Por favor, intenta más tarde.'
      case 503:
        return 'El servicio está temporalmente no disponible. Intenta más tarde.'
      default:
        if (error.code === 'ECONNABORTED') {
          return 'La solicitud tardó demasiado. Verifica tu conexión e intenta nuevamente.'
        }
        if (error.message === 'Network Error' || !error.response) {
          return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.'
        }
        return 'Ocurrió un error inesperado. Por favor, intenta nuevamente.'
    }
  }

  // Error con mensaje personalizado
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const errorMessage = error.message as string
    if (typeof errorMessage === 'string' && errorMessage.length > 0) {
      return errorMessage
    }
  }

  // Error genérico
  return 'Error al iniciar sesión. Por favor, verifica tus credenciales e intenta nuevamente.'
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
  const navigate = useNavigate()
  const { setUser } = useAuth()

  // Detectar si la sesión expiró y mostrar notificación
  useEffect(() => {
    const sessionExpired = localStorage.getItem('sessionExpired')
    if (sessionExpired === 'true') {
      toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
        position: 'top-center',
        duration: 4000,
      })
      // Limpiar el flag después de mostrar la notificación
      localStorage.removeItem('sessionExpired')
    }
  }, [])

  // Limpiar errores cuando el usuario empiece a escribir
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (fieldErrors.email) {
      setFieldErrors(prev => ({ ...prev, email: undefined }))
    }
    if (error) {
      setError(null)
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (fieldErrors.password) {
      setFieldErrors(prev => ({ ...prev, password: undefined }))
    }
    if (error) {
      setError(null)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    // Limpiar errores previos
    setError(null)
    setFieldErrors({})
    
    // Validación básica del lado del cliente
    let hasErrors = false
    const newFieldErrors: { email?: string; password?: string } = {}

    if (!email.trim()) {
      newFieldErrors.email = 'Por favor, ingresa tu email.'
      hasErrors = true
    } else {
      // Validar formato de email básico
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.trim())) {
        newFieldErrors.email = 'Por favor, ingresa un email válido.'
        hasErrors = true
      }
    }

    if (!password.trim()) {
      newFieldErrors.password = 'Por favor, ingresa tu contraseña.'
      hasErrors = true
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors)
      return
    }

    setLoading(true)

    try {
      const data = await login(email.trim(), password)
      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('user', JSON.stringify(data.user))
      
      // Actualizar el contexto de autenticación
      setUser(data.user)
      
      // Formatear nombre completo (capitalizar primera letra de cada palabra)
      const fullName = `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim()
      const formattedName = fullName.replace(/\b\w/g, (l) => l.toUpperCase())
      
      // Mostrar mensaje de éxito personalizado
      toast.success(`¡Bienvenido ${formattedName}!`, {
        position: 'top-center',
        duration: 3000,
      })
      
      onLogin(data.accessToken, data.user.role)
      navigate('/home')
    } catch (error: unknown) {
      console.error('Error en el inicio de sesión:', error)
      const friendlyMessage = getErrorMessage(error)
      setError(friendlyMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md animate-fade-in-up">
        <div className="flex flex-col items-center mb-6">
          <img 
            src={logo} 
            alt="Logo SGL" 
            className="h-16 w-auto mb-4 object-contain animate-fade-in" 
          />
        </div>
        
        {/* Error general arriba del formulario - más visible */}
        {error && (
          <div className="mb-6 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md animate-slide-down">
            <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500 mt-0.5 animate-pulse" />
            <p className="text-sm text-red-700 leading-relaxed">{error}</p>
          </div>
        )}

        {/* Email */}
        <FormField
          label="Email"
          required
          error={fieldErrors.email}
        >
          <Input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="tu@email.com"
            autoComplete="username"
            leftIcon={EnvelopeIcon}
            error={!!fieldErrors.email}
            errorMessage={fieldErrors.email}
            disabled={loading}
          />
        </FormField>

        {/* Contraseña */}
        <div className="mb-6">
          <FormField
            label="Contraseña"
            required
            error={fieldErrors.password}
          >
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              placeholder="••••••••"
              autoComplete="current-password"
              leftIcon={LockClosedIcon}
              rightIcon={showPassword ? EyeSlashIcon : EyeIcon}
              rightIconOnClick={() => setShowPassword(!showPassword)}
              error={!!fieldErrors.password}
              errorMessage={fieldErrors.password}
              disabled={loading}
            />
          </FormField>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          className="bg-custom-blue-2 text-white py-2 px-4 rounded w-full hover:bg-custom-blue transition-all duration-300 flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transform">
          {loading ? (
            <>
              <LoadingSpinner size="small" color="text-white" />
              <span>Iniciando sesión...</span>
            </>
          ) : (
            'Iniciar sesión'
          )}
        </button>
      </form>
    </div>
  )
}

export default Login
